// CanvasView マウント時に RealtimeClient を作成し、サーバからの op を doc store へ反映する。
// authMode === "mock" のときは mock realtime（自分が送った op をエコーバック）を使い、
// それ以外では WebSocket 経由で実サーバに接続する。

import { useEffect, useMemo, useState } from "react";
import { useShowToast } from "../features/canvas/notice/use-show-toast";
import { getWebConfig } from "../env";
import { createMockRealtimeClient } from "./mock-realtime";
import { computeDisconnectToastDelayMs } from "./reconnect-toast-delay";
import { createWebSocketRealtimeClient } from "./websocket-realtime";
import { useCanvasDocStore } from "../features/canvas/store/canvas-doc-store";
import { useCanvasViewersStore } from "../features/canvas/store/canvas-viewers-store";
import { useCanvasTransientStore } from "../features/canvas/store/canvas-transient-store";
import { useCanvasTimerStore } from "../features/canvas/timer/canvas-timer-store";
import { clearTransientForCommittedOp } from "../features/canvas/clear-transient-for-committed-op";
import type { RealtimeClient } from "./realtime-client";
import {
  CANVAS_TIMER_COMMAND_RETRY_WINDOW_MS,
  type CanvasObject,
  type ServerMessage,
} from "@refinement-canvas/domain";
import { useCanvasApi } from "../lib/canvas-api";

interface State {
  readonly client: RealtimeClient | null;
  readonly status: "idle" | "connecting" | "connected" | "error";
  readonly error?: Error | undefined;
}

// 予期せぬ切断からの再接続パラメータ。指数バックオフで再試行し、上限到達時のみ
// エラー状態へ落として既存の全画面リロード復旧へ委ねる。
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 15_000;
const RECONNECT_MAX_ATTEMPTS = 8;
// 再接続成功後にこの時間が経過するまで、次の切断通知を抑制する（ms）。
// 短時間の再切断でトーストがスタックしないよう、クールダウンを設ける。
const RECONNECT_TOAST_COOLDOWN_MS = 30_000;
// 切断→再接続が「裏タブ/最小化からの復帰で走る一瞬の正当な再接続」の場合、トーストを出さないための
// 遅延（ms）。裏タブ中は keepalive タイマーが凍結して API Gateway に idle 切断されるため、復帰のたびに
// 正当な再接続が走る。この時間内に再接続が完了すれば info/success とも表示せず、実際に長引く断だけ通知する。
const DISCONNECT_TOAST_DEBOUNCE_MS = 4_000;
// 欠番（pendingCommits 滞留）がこの時間埋まらなければ、恒久欠番（配信欠落）とみなして
// 権威スナップショットへの resync を能動要求する。従来は件数上限（500 件）のみで、
// 恒久欠番時は全員の確定反映が UI 通知なしで長時間停止し続けた（2026-07-06 本番障害の一因）。
// 正常時の順不同到着は数十〜数百 ms で連続化されるため、10 秒は十分に安全側の閾値。
const GAP_RESYNC_TIMEOUT_MS = 10_000;

export function useRealtimeConnection(canvasId: string): State {
  const config = getWebConfig();
  const canvasApi = useCanvasApi();
  const onServerCommit = useCanvasDocStore((s) => s.onServerCommit);
  const onServerReject = useCanvasDocStore((s) => s.onServerReject);
  const loadSnapshot = useCanvasDocStore((s) => s.loadSnapshot);
  const setViewers = useCanvasViewersStore((s) => s.setViewers);
  const setCursor = useCanvasViewersStore((s) => s.setCursor);
  const setViewerView = useCanvasViewersStore((s) => s.setView);
  const resetViewers = useCanvasViewersStore((s) => s.reset);
  const setTimer = useCanvasTimerStore((s) => s.setTimer);
  const rejectTimerCommand = useCanvasTimerStore((s) => s.rejectCommand);
  const resetTimer = useCanvasTimerStore((s) => s.reset);
  const beginTimerSync = useCanvasTimerStore((s) => s.beginTimerSync);
  const showToast = useShowToast();

  // クライアント自身は再生成したくないので useMemo + キーで安定させる
  const client = useMemo<RealtimeClient>(() => {
    if (config.authMode === "mock") {
      return createMockRealtimeClient();
    }
    return createWebSocketRealtimeClient({ websocketUrl: config.realtimeWebSocketUrl });
  }, [config.authMode, config.realtimeWebSocketUrl]);

  const [status, setStatus] = useState<State["status"]>("idle");
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setStatus("connecting");
    beginTimerSync();

    // サーバ権威スナップショットを REST で取り直し、確定状態へ収束させる。op.reject / resync.required /
    // 順序バッファ overflow（pendingCommits フラッシュ）で共用する。再接続経路の rebootstrap と異なり、
    // 失敗は投げずログのみに留める（いずれも単発の収束要求で、再接続のリトライ制御対象ではないため）。
    const resyncFromSnapshot = async (context: string): Promise<void> => {
      try {
        const data = await canvasApi.fetchCanvasBootstrap(canvasId);
        if (cancelled || data.canvasId !== canvasId) return;
        const applied = loadSnapshot({
          canvasId: data.canvasId,
          serverSeq: data.serverSeq,
          objects: [...data.objects],
          meta: data.meta,
        });
        // snapshot 経由で committed 化した pending op のゴーストを破棄する。
        applied.forEach(clearTransientForCommittedOp);
      } catch (e) {
        if (cancelled) return;
        console.warn(`[realtime] ${context} 後の bootstrap に失敗`, e);
      }
    };

    // サーバからの message を doc store / viewers store に橋渡しする
    const unsubscribe = client.onMessage((message: ServerMessage) => {
      switch (message.type) {
        case "op.commit": {
          // onServerCommit は「今回 committed へ実際に畳み込んだ op」群を返す。順不同で先着して
          // バッファ保留しただけの commit は含まれない。確定済みオブジェクトへ反映された op に対してのみ
          // 操作中ゴースト（一時プレビュー）を破棄し、二重表示や「保留中に他者操作が一瞬消える」ちらつきを防ぐ。
          const appliedOps = onServerCommit(message.op, message.serverSeq);
          for (const applied of appliedOps) {
            clearTransientForCommittedOp(applied);
          }
          return;
        }
        case "op.reject":
          onServerReject(message.clientOpId);
          showToast(
            "warning",
            message.reason ||
              "操作を保存できませんでした。必要に応じて新しいキャンバスへ分割してください。"
          );
          // 楽観更新済みの表示をサーバ権威状態へ戻すため、拒否時は最新スナップショットを取得する。
          void resyncFromSnapshot("op.reject");
          return;
        case "bootstrap.snapshot": {
          const applied = loadSnapshot({
            canvasId,
            serverSeq: message.serverSeq,
            objects: message.objects as CanvasObject[],
          });
          // snapshot 経由で committed 化した pending op のゴーストを破棄する。
          applied.forEach(clearTransientForCommittedOp);
          return;
        }
        case "viewer.snapshot":
          setViewers(message.viewers);
          return;
        case "viewer.cursor":
          if (message.cursor) {
            setCursor(message.userId, {
              xRatio: message.cursor.xRatio,
              yRatio: message.cursor.yRatio,
              updatedAt: Date.now(),
            });
          } else {
            setCursor(message.userId, null);
          }
          return;
        case "viewer.view":
          // フォーカスモード用の他者ビュー更新。永続化しない情報なので最新値だけを保持する。
          if (message.view) {
            setViewerView(message.userId, {
              panX: message.view.panX,
              panY: message.view.panY,
              zoom: message.view.zoom,
              updatedAt: Date.now(),
            });
          } else {
            setViewerView(message.userId, null);
          }
          return;
        case "op.transient": {
          // 他参加者の操作中プレビュー（ゴースト）。永続化せず transient store に反映し、
          // canvas-remote-previews-layer がリアルタイム描画する。
          const transient = useCanvasTransientStore.getState();
          if (message.op.type === "object.transient.transform") {
            transient.applyRemoteTransform(message.fromUserId, message.op);
          } else if (message.op.type === "pen.stroke.append") {
            transient.appendRemotePen(message.fromUserId, message.op);
          } else if (message.op.type === "object.text.cursor") {
            // 他参加者のテキスト編集キャレット位置。canvas-remote-carets-layer が描画する。
            transient.applyRemoteTextCursor(message.fromUserId, message.op);
          }
          return;
        }
        case "timer.sync":
          setTimer({
            timer: message.timer,
            serverNowEpochMs: message.serverNowEpochMs,
            localReceivedAtEpochMs: Date.now(),
            ...(message.commandId !== undefined ? { commandId: message.commandId } : {}),
          });
          return;
        case "timer.reject":
          rejectTimerCommand(message.commandId);
          // ロールバック後も、別参加者による直近更新を取りこぼさないよう権威状態を再要求する。
          client.sendMessage({ type: "timer.request" });
          showToast("warning", message.reason || "タイマーを更新できませんでした。");
          return;
        case "resync.required":
          // サーバ再起動 / シーケンス欠落時は REST bootstrap で最新スナップショットを取り直す。
          // resyncFromSnapshot が cancelled フラグとレスポンスの canvasId 一致を二重確認してから
          // store を上書きするため、画面遷移・canvas 切替後にレスポンスが返っても誤上書きしない。
          console.warn("[realtime] resync.required:", message.reason);
          void resyncFromSnapshot("resync.required");
          return;
      }
    });

    // 順序バッファ（pendingCommits）が上限に達して欠番を取りこぼしたとき（恒久的な配信欠落の疑い）、
    // doc store が resyncRequestSeq を進める。その increment を検知し、権威スナップショットへ resync して
    // 確定状態を収束させる（欠番は取りこぼすと以後 committed へ入らないため、能動的に取り直す）。
    let lastResyncRequestSeq = useCanvasDocStore.getState().resyncRequestSeq;
    const unsubscribeResync = useCanvasDocStore.subscribe((state) => {
      if (state.resyncRequestSeq > lastResyncRequestSeq) {
        lastResyncRequestSeq = state.resyncRequestSeq;
        console.warn("[realtime] pendingCommits の欠番回収を検知: REST bootstrap で resync します");
        void resyncFromSnapshot("pendingCommits gap");
      }
    });

    // 欠番の時間ベース検知。件数上限（500 件）だけに依存すると、恒久欠番（採番済みなのに配信されない
    // op）が生じた場合に全員の確定反映が長時間停止する。水位（serverSeq）が止まったまま欠番バッファが
    // 残り続けたら、一定時間で requestGapResync を呼び、上と同じ resync 経路（resyncRequestSeq）に乗せる。
    let gapTimer: ReturnType<typeof setTimeout> | null = null;
    let gapWatermark = -1; // 欠番検知時の水位。水位が進んだら新しいエピソードとして張り直す。
    const clearGapTimer = (): void => {
      if (gapTimer !== null) {
        clearTimeout(gapTimer);
        gapTimer = null;
      }
    };
    const evaluateCommitGap = (state: {
      readonly pendingCommits: Record<number, unknown>;
      readonly serverSeq: number;
    }): void => {
      const hasGap = Object.keys(state.pendingCommits).length > 0;
      if (!hasGap) {
        clearGapTimer();
        gapWatermark = -1;
        return;
      }
      if (state.serverSeq === gapWatermark && gapTimer !== null) return; // 同一エピソードの継続
      gapWatermark = state.serverSeq;
      clearGapTimer();
      gapTimer = setTimeout(() => {
        gapTimer = null;
        console.warn(
          `[realtime] 欠番が ${GAP_RESYNC_TIMEOUT_MS}ms 以上埋まりません。権威スナップショットへ resync します`
        );
        useCanvasDocStore.getState().requestGapResync();
      }, GAP_RESYNC_TIMEOUT_MS);
    };
    const unsubscribeGapWatch = useCanvasDocStore.subscribe(evaluateCommitGap);
    evaluateCommitGap(useCanvasDocStore.getState());

    // 1 回分の接続処理。required 環境では都度フレッシュなチケットを発行する
    // （チケットは短命なため、再接続のたびに新しいものが必要）。
    // チケット発行・接続の各 await 後に cancelled を再確認し、effect の cleanup 後に
    // ソケットを張ってしまう（受信・close 購読も無いゴースト接続のリーク）のを防ぐ。
    const connectOnce = async (): Promise<void> => {
      let ticket: string | undefined;
      if (config.authMode !== "mock") {
        const session = await canvasApi.issueRealtimeSession(canvasId);
        ticket = session.token;
      }
      // チケット発行待ちの間に unmount / canvas 切替で cleanup 済みなら、接続を張らない。
      if (cancelled) return;
      await client.connect(canvasId, ticket);
      // 接続確立後に取消されていた場合は、張ったソケットを即座に閉じて破棄する。
      if (cancelled) {
        client.disconnect();
        return;
      }
      // 応答前に接続が切れたタイマー操作は、期限内だけ同じ commandId で再送して収束させる。
      // サーバ側は再送期限より長く操作 ID 行を保持するため、送信待ちキューと重複しても二重適用されない。
      let pendingTimerCommand = useCanvasTimerStore.getState().pendingCommand;
      if (
        pendingTimerCommand &&
        Date.now() - pendingTimerCommand.createdAtEpochMs >= CANVAS_TIMER_COMMAND_RETRY_WINDOW_MS
      ) {
        if (rejectTimerCommand(pendingTimerCommand.commandId)) {
          showToast(
            "warning",
            "タイマー操作の確認がタイムアウトしたため、最新の共有状態へ戻しました。"
          );
        }
        pendingTimerCommand = undefined;
      }
      if (pendingTimerCommand) {
        client.sendMessage({
          type: "timer.command",
          command: pendingTimerCommand.command,
          commandId: pendingTimerCommand.commandId,
        });
      }
      // 接続・再接続のたびにサーバ権威の共有タイマー状態を取得する。
      // 終了予定時刻を受け取るため、途中参加や切断復帰でも全員が同じ残り時間へ収束する。
      client.sendMessage(
        pendingTimerCommand
          ? { type: "timer.request", pendingCommandId: pendingTimerCommand.commandId }
          : { type: "timer.request" }
      );
    };

    // 再接続成功後、切断中に取りこぼしたサーバ op を反映するため最新スナップショットを取り直す。
    // doc store は同一 canvas なら未 ack の楽観 op（inFlightOps）を再適用して保持するため、
    // 編集途中のローカル変更は失われない。
    // ここで失敗を握りつぶすと「取りこぼし未回収のまま接続済み」に見えてサイレントな同期欠落が
    // 残るため、例外はそのまま投げて再接続処理（reconnect）側の再試行 / エラー遷移に委ねる。
    const rebootstrap = async (): Promise<void> => {
      const data = await canvasApi.fetchCanvasBootstrap(canvasId);
      if (cancelled || data.canvasId !== canvasId) return;
      const applied = loadSnapshot({
        canvasId: data.canvasId,
        serverSeq: data.serverSeq,
        objects: [...data.objects],
        meta: data.meta,
      });
      // snapshot 経由で committed 化した pending op のゴーストを破棄する。
      applied.forEach(clearTransientForCommittedOp);
    };

    let reconnectAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // 1 回の切断エピソードでトーストを多重表示（多重予約）しないためのフラグ。
    let disconnectNotified = false;
    // 切断通知トーストの遅延タイマー。裏タブ/最小化からの復帰で走る一瞬の再接続ではトーストを出さないよう、
    // info トーストを DISCONNECT_TOAST_DEBOUNCE_MS だけ遅延表示し、その間に再接続が成功したら取り消す。
    let disconnectToastTimer: ReturnType<typeof setTimeout> | null = null;
    // info トーストを実際に表示したか。表示前（デバウンス中）に復帰した場合はペアの success トーストも
    // 出さない（「切れました」を出さずに「再接続しました」だけ出る不整合を防ぐ）。
    let disconnectToastShown = false;
    // 最後の再接続成功から RECONNECT_TOAST_COOLDOWN_MS が経過するまで、次の切断通知を抑制する期限。
    // 成功トーストを表示したかどうかに関わらず、再接続成功のたびに更新される。
    let reconnectToastCooldownUntil = 0;
    // 再接続試行の世代。新しい切断（onClose）が来るたびに +1 し、進行中の古い試行を無効化する。
    // これにより、再接続中にさらに切断された場合でも、古い試行が成功扱いになったり
    // 再接続タイマーが多重化したりするのを防ぐ。
    let reconnectEpoch = 0;
    // connectOnce が成功した世代。同一世代での再試行（rebootstrap 失敗など）では健全なソケットを
    // 張り替えず、取りこぼし回収（rebootstrap）だけを再試行する。真の切断（onClose で epoch++）が
    // 起きると陳腐化し、connectOnce を再実行してソケットを張り直す。
    let socketReadyForEpoch = -1;

    const clearReconnectTimer = (): void => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    // 次の再接続を 1 件だけ予約する。試行回数をここで increment し、上限に達したらエラー状態へ
    // 落として既存の全画面リロード復旧へ委ねる。reconnectAttempts は「最後に再接続が完全成功して
    // から積み上がった連続試行数」で、完全成功時のみ 0 へ戻す（onClose では戻さない）。これにより
    // 「open 後・rebootstrap 完了前にさらに切断」を繰り返しても上限到達でフォールバックでき、
    // 無限再接続ループ・送信キュー肥大に陥らない。常に最新の試行だけが残るよう既存タイマーは消す。
    const scheduleReconnect = (): void => {
      if (cancelled) return;
      clearReconnectTimer();
      reconnectAttempts += 1;
      if (reconnectAttempts > RECONNECT_MAX_ATTEMPTS) {
        // 再接続が継続的に失敗／切断し続けるときは、エラー状態にして全画面リロード復旧へ委ねる。
        setStatus("error");
        setError(new Error("共同編集の再接続が繰り返し失敗しました。"));
        return;
      }
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempts - 1),
        RECONNECT_MAX_DELAY_MS
      );
      reconnectTimer = setTimeout(() => {
        void reconnect();
      }, delay);
    };

    // 予約された再接続を 1 回試みる。成功（connectOnce＋rebootstrap）で完了、失敗時は再予約する。
    async function reconnect(): Promise<void> {
      if (cancelled) return;
      // この試行の世代を固定する。await の合間に新しい切断（onClose）が来て世代が進んだら、
      // この試行は陳腐化したものとして以降の状態更新・再スケジュールを行わない。
      const epoch = reconnectEpoch;
      try {
        // 同一世代でソケットが既に確立済みなら、connectOnce をやり直して健全なソケットを張り替えない。
        // rebootstrap（REST）だけが一時失敗したケースでは、$disconnect/$connect の再発行・チケット再取得・
        // presence 再登録を伴うソケット張り替えを避け、取りこぼし回収だけを再試行する。真の切断時は
        // onClose が reconnectEpoch を進めるため socketReadyForEpoch が陳腐化し、connectOnce が再実行される。
        if (socketReadyForEpoch !== epoch) {
          await connectOnce();
          if (cancelled || epoch !== reconnectEpoch) return;
          socketReadyForEpoch = epoch;
        }
        // 取りこぼし回収（再 bootstrap）が成功して初めて「再接続完了」とみなす。
        // ここで失敗すれば下の catch で再予約し、connect() が既存ソケットを静かに張り替えるため
        // 二重接続にはならない。成功トーストも回収成功後に出す。
        await rebootstrap();
        if (cancelled || epoch !== reconnectEpoch) return;
        // 切断中に行った編集は、WebSocket クライアントの送信待ちキュー（永続 op は破棄せず、
        // 送信途中の切断でも残す）に積まれており、再接続の open 時に送り直される。ここで
        // doc store の inFlightOps を一括再送することはしない——inFlightOps には「作成者だけに
        // 見せるローカル専用 op（送信しない attachment.update など）」が含まれ得るうえ、サーバは
        // clientOpId による重複排除を行わないため、キュー送信分との二重送信で create/commit 系が
        // 偽の reject を起こすため。なお「接続中に送信した直後・ack 前にネットワーク断で当該 op が
        // サーバ未達のまま失われる」稀なケースは、本フローでは回収できない既知の残存制限である
        // （元実装は再接続自体が無く全 op を喪失していたため、本対応で大幅に改善している）。
        // 完全な回収にはサーバ側の clientOpId 冪等化と送信側 ack 追跡の導入が必要で、別対応とする。
        reconnectAttempts = 0; // 完全成功時のみリセット
        setStatus("connected");
        // 再接続成功のたびにクールダウンを延長する（disconnectNotified の値に関わらず更新）。
        // こうすることで、クールダウン中に再切断→再接続が繰り返されても期限が都度リセットされ
        // 30 秒超後のフラップで問題が再発するのを防ぐ。
        reconnectToastCooldownUntil = Date.now() + RECONNECT_TOAST_COOLDOWN_MS;
        // まだ表示前の切断トースト予約があれば取り消す（復帰が速ければ「切れました」を出さない）。
        if (disconnectToastTimer !== null) {
          clearTimeout(disconnectToastTimer);
          disconnectToastTimer = null;
        }
        // info トーストを実表示したエピソードだけ success を出す（デバウンス中の復帰は info/success とも無音）。
        if (disconnectToastShown) {
          showToast("success", "共同編集に再接続しました。");
        }
        disconnectNotified = false;
        disconnectToastShown = false;
      } catch (e) {
        // 陳腐化した試行は何もしない（最新試行が別途進行している）。
        if (cancelled || epoch !== reconnectEpoch) return;
        // connectOnce / rebootstrap の失敗。次の試行を予約する（回数 increment と上限判定は内部で行う）。
        scheduleReconnect();
      }
    }

    // 接続確立後の予期せぬ切断を検知して再接続を開始する。
    // 切断中もキャンバスは表示したまま（status は connected のまま）、背後で再接続する。
    const unsubscribeClose = client.onClose(() => {
      if (cancelled) return;
      // 切断中に stale な共有状態を基準とした操作をキューへ積まないよう、再同期完了まで停止する。
      beginTimerSync();
      // 新しい切断エピソード。世代を進めて進行中の古い試行を無効化し、再接続を予約する。
      // reconnectAttempts はここでは 0 に戻さない（完全成功時のみ戻す）。これにより
      // 「open 後・rebootstrap 完了前の再切断」が続いても上限到達でフォールバックできる。
      reconnectEpoch += 1;
      if (!disconnectNotified) {
        // この切断エピソードで info トーストを一度だけ予約する（多重予約しない）。実際の表示は
        // 「デバウンス」と「クールダウン残り時間」のうち長い方の後で、その前に再接続が成功すれば
        // 表示せずに終わる（無音復帰）。クールダウン中に始まった切断でも、復旧しなければ最終的に
        // 通知されるようにするため（無通知のまま放置しない）、クールダウン中でも予約自体は行う。
        disconnectNotified = true;
        const delay = computeDisconnectToastDelayMs(
          Date.now(),
          reconnectToastCooldownUntil,
          DISCONNECT_TOAST_DEBOUNCE_MS
        );
        disconnectToastTimer = setTimeout(() => {
          disconnectToastTimer = null;
          disconnectToastShown = true;
          showToast("info", "共同編集の接続が切れました。再接続しています…");
        }, delay);
      }
      scheduleReconnect();
    });

    void (async () => {
      try {
        // 初回接続。チケット発行に失敗した場合は接続できないため、エラー扱いとする。
        await connectOnce();
        if (cancelled) return;
        setStatus("connected");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      if (disconnectToastTimer !== null) clearTimeout(disconnectToastTimer);
      clearGapTimer();
      unsubscribe();
      unsubscribeResync();
      unsubscribeGapWatch();
      unsubscribeClose();
      client.disconnect();
      resetViewers();
      resetTimer();
      useCanvasTransientStore.getState().reset();
    };
  }, [
    canvasApi,
    beginTimerSync,
    canvasId,
    client,
    config.authMode,
    loadSnapshot,
    onServerCommit,
    onServerReject,
    setViewers,
    setCursor,
    resetViewers,
    rejectTimerCommand,
    resetTimer,
    setTimer,
    showToast,
  ]);

  return { client, status, error };
}
